import { useQueryClient } from '@tanstack/react-query'
import { SortingState } from '@tanstack/react-table'
import { useDebounce } from '@uidotdev/usehooks'
import { UserOrganizationStatus, UserStatus } from '@/constants/user'
import { DlInstance } from '@/lib/axios'
import { useMutation, useQuery } from '@/hooks'
import { datatableSortingStateToQuery } from '@/utils/api'
import { DataResponse, PaginatedResponse } from '@/models/api'
import { normalizeStringList } from '@/utils/normalizeStringList'
import { SizeStatus } from '@/constants/size'
import axios, { AxiosError } from 'axios'
import { Bill } from '@/models/bill/bill'
import { getSavedPdfFontScale } from '@/lib/fontScale'

type UseGetBill = {
  page?: number
  pageSize?: number
  sort?: SortingState
  search?: string
  status?: string
  sale_mode?: string
  bill_date_from?: string
  bill_date_to?: string
  location?: string
  print_status?: string
  payment_method?: string
  payment_status?: string
  outstanding?: boolean
  enabled?: boolean
}

export const useGetBillList = ({
  page,
  pageSize,
  sort,
  search,
  status,
  sale_mode,
  bill_date_from,
  bill_date_to,
  location,
  print_status,
  payment_method,
  payment_status,
  outstanding,
  enabled,
}: UseGetBill) => {
  return useQuery<PaginatedResponse<Bill>>({
    queryKey: [
      'bills',
      {
        page,
        pageSize,
        sort,
        search,
        status,
        sale_mode,
        bill_date_from,
        bill_date_to,
        location,
        print_status,
        payment_method,
        payment_status,
        outstanding,
      },
    ],
    url: '/bills',
    axiosConfig: {
      params: {
        page,
        pageSize,
        search,
        bill_type: status,
        sale_mode,
        bill_date_from,
        bill_date_to,
        location,
        print_status,
        payment_method,
        payment_status,
        outstanding: outstanding ? 'true' : undefined,
        sort: datatableSortingStateToQuery(sort),
      },
    },
    queryConfig: {
      enabled,
    },
  })
}

interface TotalReport {
  total_bill: number
  total_quantity: number
  total_price: number
}

export const useGetBillTotal = ({
  page,
  pageSize,
  sort,
  search,
  status,
  sale_mode,
  bill_date_from,
  bill_date_to,
  location,
  print_status,
  payment_method,
  payment_status,
  outstanding,
  enabled,
}: UseGetBill) => {
  return useQuery<DataResponse<TotalReport>>({
    queryKey: [
      'bills_total_report',
      {
        page,
        pageSize,
        sort,
        search,
        status,
        sale_mode,
        bill_date_from,
        bill_date_to,
        location,
        print_status,
        payment_method,
        payment_status,
        outstanding,
      },
    ],
    url: '/bills/total-report',
    axiosConfig: {
      params: {
        page,
        pageSize,
        search,
        bill_type: status,
        sale_mode,
        bill_date_from,
        bill_date_to,
        location,
        print_status,
        payment_method,
        payment_status,
        outstanding: outstanding ? 'true' : undefined,
      },
    },
    queryConfig: {
      enabled,
    },
  })
}

export const useGetBillById = (id: string) => {
  return useQuery<Bill>({
    queryKey: ['bills', id],
    url: `/bills/${id}`,
    queryConfig: {
      enabled: !!id,
      select: (payload: { data?: Bill } & Bill) => {
        if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
          return payload.data as Bill
        }
        return payload as unknown as Bill
      },
    },
  })
}

export type CreateBillRequest = {
  bill_date: string
  location: string
  customer_information: {
    name: string
    phone?: string
    car_registration?: string
    address?: string
    tax_id?: string
    brand_mark?: string
  }
  /** Weight-first lines (preferred) */
  line_items?: Array<{
    durian_type?: string
    grade?: string
    size?: string
    weight_kg: number
    price_per_kg: number
    description?: string
    box_count?: number
    weight_per_box_kg?: number
    strap_band?: string
    strap_quantity?: number
  }>
  /** Legacy basket-style (optional if line_items set) */
  order_information?: {
    description?: string
    size: string
    product_price: number
    cycles: {
      product_quantity: number
    }[]
  }[]
  payment_information: {
    payment_method: string
    payment_status: string
    slip_path?: string
    slip_url?: string
  }
  bill_information: {
    bill_type: string
    sale_mode?: string
    buy_party_type?: 'stall' | 'orchard'
    container_no?: string
    customer_sequence_no?: string
    discount_baht?: number
    extra_charges?: Array<{
      expense_type_id?: string
      expense_type_name?: string
      quantity?: number
      unit_price_baht?: number
      amount_baht?: number
    }>
  }
}

export const useCreateBill = () => {
  const queryClient = useQueryClient()
  return useMutation<any, Error, CreateBillRequest>(
    ['create_bills'],
    {
      url: `/bills/create`,
      method: 'POST',
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['bills'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard_chart'] })
      },
    },
  )
}

export type UpdateBillRequest = {
  bill_date: string
  location: string
  customer_information?: {
    name?: string
    phone?: string
    car_registration?: string
    address?: string
    tax_id?: string
    brand_mark?: string
  }
  line_items?: Array<{
    durian_type?: string
    grade?: string
    size?: string
    weight_kg: number
    price_per_kg: number
    description?: string
    box_count?: number
    weight_per_box_kg?: number
    strap_band?: string
    strap_quantity?: number
  }>
  order_information?: {
    size?: string
    product_price?: number
    cycles?: {
      product_quantity?: number
    }[]
  }[]
  payment_information?: {
    payment_method?: string
    payment_status?: string
    slip_path?: string
    slip_url?: string
  }
  bill_information: {
    bill_type?: string
    sale_mode?: string
    buy_party_type?: 'stall' | 'orchard'
    container_no?: string
    customer_sequence_no?: string
    discount_baht?: number
    extra_charges?: Array<{
      expense_type_id?: string
      expense_type_name?: string
      quantity?: number
      unit_price_baht?: number
      amount_baht?: number
    }>
  }
  print_status?: string
}

export const useUpdateBill = (id: string) => {
  const queryClient = useQueryClient()
  return useMutation<any, Error, UpdateBillRequest>(
    ['update_bills'],
    {
      url: `/bills/${id}`,
      method: 'PATCH',
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['bills'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard_chart'] })
      },
    },
  )
}

export const useDeleteBill = () => {
  const queryClient = useQueryClient()
  return useMutation<any, Error, { id: string }>(
    ['delete_bills'],
    { url: `/bills/:id`, method: 'DELETE' },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['bills'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard_chart'] })
      },
    },
  )
}

export type CreateBillPaymentRequest = {
  paid_amount: number
  payment_method: string
  paid_at?: string
  slip_path?: string
  slip_url?: string
  note?: string
}

export const useCreateBillPaymentRound = (billId: string) => {
  const queryClient = useQueryClient()
  return useMutation<Bill, Error, CreateBillPaymentRequest>(
    ['create_bill_payment', billId],
    {
      url: `/bills/${billId}/payments`,
      method: 'POST',
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['bills'] })
        queryClient.invalidateQueries({ queryKey: ['bills_pdf', billId], exact: false })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard_chart'] })
      },
    },
  )
}

export const useGeneratePDF = (id: string, paymentRoundNo?: number) => {
  const pdfFontScale = getSavedPdfFontScale()
  const query = useQuery<Blob>({
    queryKey: ['bills_pdf', id, paymentRoundNo ?? 'latest', pdfFontScale],
    url: `/bills/${id}/pdf`,
    axiosConfig: {
      responseType: 'blob',
      params: {
        ...(paymentRoundNo != null && Number.isFinite(paymentRoundNo)
          ? { payment_round: paymentRoundNo }
          : {}),
        pdf_font_scale: pdfFontScale,
      },
    },
  })

  return query
}

/** Open installment receipt PDF in a new window and trigger print dialog. */
export type OutstandingBillSummary = {
  bill_id: string
  bill_date: string
  bill_number: string | null
  total_amount: number
  remaining_amount: number
  paid_amount: number
}

export type BulkPaymentAllocation = {
  bill_id: string
  bill_date: string
  bill_number: string | null
  allocated_amount: number
  remaining_before: number
  remaining_after: number
  closes_fully: boolean
  bill_total?: number
  round_no?: number
}

export type BulkBillPaymentRequest = CreateBillPaymentRequest & {
  customer_id: string
  bill_type: string
  location: string
}

export type BulkPaymentPreviewResponse = {
  paid_amount: number
  total_outstanding: number
  total_allocated: number
  allocations: BulkPaymentAllocation[]
}

export type BulkPaymentCommitResponse = {
  batch_id: string
  paid_amount: number
  allocations: BulkPaymentAllocation[]
}

export const useGetOutstandingByCustomer = (
  customerId: string | undefined,
  billType: string,
  location: string | undefined,
) => {
  return useQuery<
    DataResponse<{
      location: string
      bills: OutstandingBillSummary[]
      bill_count: number
      total_outstanding: number
    }>
  >({
    queryKey: ['bills_outstanding_by_customer', customerId, billType, location],
    url: '/bills/outstanding-by-customer',
    axiosConfig: {
      params: {
        customer_id: customerId,
        bill_type: billType,
        location,
      },
    },
    queryConfig: {
      enabled: !!customerId && !!billType && !!location,
    },
  })
}

export const usePreviewBulkBillPayment = () => {
  return useMutation<DataResponse<BulkPaymentPreviewResponse>, Error, BulkBillPaymentRequest>(
    ['bulk_bill_payment_preview'],
    {
      url: '/bills/bulk-payment/preview',
      method: 'POST',
    },
  )
}

export const useCommitBulkBillPayment = () => {
  const queryClient = useQueryClient()
  return useMutation<DataResponse<BulkPaymentCommitResponse>, Error, BulkBillPaymentRequest>(
    ['bulk_bill_payment_commit'],
    {
      url: '/bills/bulk-payment',
      method: 'POST',
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['bills'] })
        queryClient.invalidateQueries({ queryKey: ['bills_outstanding_by_customer'] })
        queryClient.invalidateQueries({ queryKey: ['bulk_payment_history'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard_chart'] })
      },
    },
  )
}

function openPdfBlobForPrint(blob: Blob) {
  const url = URL.createObjectURL(blob)
  const printWindow = window.open(url, '_blank')
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.print()
    }
  }
}

export type BulkPaymentHistoryItem = {
  id: string
  customer_id: string
  customer_name: string
  location: string
  bill_type: string
  paid_amount: number
  payment_method: string
  paid_at: string
  note?: string | null
  bill_count: number
  created_at: string
  created_by_name: string | null
}

export type BulkPaymentBatchDetail = BulkPaymentHistoryItem & {
  customer_phone?: string
  customer_car?: string
  allocations: BulkPaymentAllocation[]
}

type BulkPaymentHistoryParams = {
  page?: number
  pageSize?: number
  customer_id?: string
  location?: string
  bill_type?: string
  paid_at_from?: string
  paid_at_to?: string
}

export const useGetBulkPaymentHistory = (params: BulkPaymentHistoryParams) => {
  return useQuery<PaginatedResponse<BulkPaymentHistoryItem>>({
    queryKey: ['bulk_payment_history', params],
    url: '/bills/bulk-payment/history',
    axiosConfig: { params },
  })
}

export const useGetBulkPaymentBatch = (batchId: string | undefined) => {
  return useQuery<DataResponse<BulkPaymentBatchDetail>>({
    queryKey: ['bulk_payment_batch', batchId],
    url: `/bills/bulk-payment/${batchId}`,
    queryConfig: { enabled: !!batchId },
  })
}

/** One PDF file: summary + per-bill receipts (พิมพ์ครั้งเดียว). */
export async function printBulkPaymentPdf(batchId: string) {
  const pdfFontScale = getSavedPdfFontScale()
  const res = await DlInstance.get<Blob>(`/bills/bulk-payment/${batchId}/pdf`, {
    params: {
      pdf_font_scale: pdfFontScale,
    },
    responseType: 'blob',
  })
  const blob = res.data
  if (blob instanceof Blob) {
    openPdfBlobForPrint(blob)
  }
}

/** @deprecated Use printBulkPaymentPdf — server merges all pages into one file. */
export async function printBulkSettlementPdfs(batchId: string) {
  await printBulkPaymentPdf(batchId)
}

export async function printBillPdf(billId: string, paymentRoundNo?: number) {
  const pdfFontScale = getSavedPdfFontScale()
  const params: Record<string, string | number> = {}
  if (paymentRoundNo != null && Number.isFinite(paymentRoundNo)) {
    params.payment_round = paymentRoundNo
  }
  params.pdf_font_scale = pdfFontScale
  const res = await DlInstance.get<Blob>(`/bills/${billId}/pdf`, {
    params,
    responseType: 'blob',
  })
  const blob = res.data
  if (blob instanceof Blob) {
    openPdfBlobForPrint(blob)
  }
}

export type WholesaleCustomerProfile = {
  name: string
  phone: string
  car_registration: string
  address: string
  tax_id: string
  brand_mark: string
}

export type WholesaleCustomerField = 'address' | 'tax_id' | 'brand_mark'

export async function fetchWholesaleCustomerProfile(
  customerName: string,
): Promise<WholesaleCustomerProfile | null> {
  const res = await DlInstance.get<{ data: WholesaleCustomerProfile | null }>(
    '/bills/wholesale-customer-profile',
    { params: { customer_name: customerName.trim() } },
  )
  return res.data?.data ?? null
}

export const useWholesaleCustomerFieldSuggestions = (
  customerName: string,
  field: WholesaleCustomerField,
  query: string,
  enabled: boolean,
) => {
  const debouncedName = useDebounce(customerName.trim(), 300)
  const debouncedQ = useDebounce(query.trim(), 250)

  return useQuery<string[]>({
    queryKey: [
      'bills',
      'wholesale-customer-field-suggestions',
      debouncedName,
      field,
      debouncedQ,
    ],
    url: '/bills/wholesale-customer-field-suggestions',
    axiosConfig: {
      params: {
        customer_name: debouncedName,
        field,
        q: debouncedQ || undefined,
      },
    },
    queryConfig: {
      enabled: enabled && debouncedName.length > 0,
      queryFn: async ({ signal }) => {
        const result = await DlInstance.get<{ data?: unknown }>(
          '/bills/wholesale-customer-field-suggestions',
          {
            params: {
              customer_name: debouncedName,
              field,
              q: debouncedQ || undefined,
            },
            signal,
          },
        )
        return normalizeStringList(result.data)
      },
    },
  })
}

// ── Year PDF export ──

export type ExportYearPdfParams = {
  year: number
  bill_type: 'buy' | 'sell'
  template: string
  pdf_mode: 'report' | 'receipts' | 'csv'
  month?: number
  location?: string
  pdf_font_scale?: number
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function exportBillsYearPdf(params: ExportYearPdfParams) {
  const pdfFontScale = params.pdf_font_scale ?? getSavedPdfFontScale()
  const payload = {
    ...params,
    pdf_font_scale: pdfFontScale,
  }
  const res = await DlInstance.post<Blob>('/bills/export/year-pdf', payload, {
    responseType: 'blob',
    timeout: 300_000,
  })
  const blob = res.data
  if (blob instanceof Blob) {
    const monthPart = params.month != null ? `-${String(params.month).padStart(2, '0')}` : ''
    const filename = `bills-export-${params.year}${monthPart}-${params.bill_type}-${params.template}-${params.pdf_mode}.zip`
    downloadBlob(filename, blob)
  }
}
